import { useId } from "react";

import { Box } from "../../components/ui/box";
import { Icon, type IconName } from "../../components/ui/icon";
import type { Color } from "../../lib/colors";
import type { Omittable } from "../../lib/props";

/**
 * Gallery tile art — the small illustrative mockup a catalog entry shows
 * instead of a live render, plus the gradient icon square that labels a row.
 *
 * The art is NOT a chart: it reads no data. But it is still UI, so it paints
 * from `tokens.css` like everything else. `data-color` on the root `<svg>`
 * binds the entry's deco color to `--c`, and every mark below is a `var()` or a
 * `color-mix()` of it — which means the art follows a theme flip live, with no
 * JS resolution, and every one of the 25 color names works rather than the ten
 * a literal table could be bothered to list.
 *
 * Consumers pick a `variant` from the vocabulary below and a deco `color`.
 */

/**
 * The deco ramp: four steps of the entry's color, light to dark.
 *
 * The tile body is `--card`, so the light end mixes toward it and the dark end
 * mixes toward `--foreground`. Both ends therefore stay legible against the
 * body in either mode without either end being written twice.
 */
const RAMP = {
  lightest: "color-mix(in oklab, var(--c) 32%, var(--card))",
  lighter: "color-mix(in oklab, var(--c) 62%, var(--card))",
  base: "var(--c)",
  darker: "color-mix(in oklab, var(--c) 72%, var(--foreground))",
} as const;

/** The foreground palette the art is drawn in — the widget chrome it depicts, not the deco. */
const FOREGROUND = {
  /** The tile body the art is drawn on, and the donut's center punch-out. */
  body: "var(--card)",
  /** The tile body's rim. */
  rim: "var(--card-border)",
  /** Divider between rows of a list. */
  hairline: "var(--border)",
  /** The embed's inner frame — a well inside the body. */
  well: "var(--sunken)",
  /** Backing bars, axis rules, unfilled tracks. */
  track: "var(--muted)",
  /** A secondary bar: one step louder than `track`. */
  secondary: "color-mix(in oklab, var(--muted-foreground) 45%, var(--muted))",
  /** Label bars — the text stand-in in a list or metric row. */
  label: "var(--muted-foreground)",
  /** The one loud element: a headline bar, or the metric numeral. */
  loud: "var(--card-foreground)",
} as const;

/** The embed's window controls, in their conventional order. */
const WINDOW_DOTS = ["var(--destructive)", "var(--warning)", "var(--success)"] as const;

/** The art vocabulary. `fallback` draws the entry's own icon instead. */
export type TilePreviewVariant =
  | "pie"
  | "donut"
  | "bar"
  | "line"
  | "battery"
  | "metric"
  | "heatmap"
  | "treemap"
  | "list"
  | "activity"
  | "note"
  | "embed"
  | "fallback";

export type TilePreviewArtProps = Omittable<{
  variant: TilePreviewVariant;
  /** Deco color name; grey when unset. */
  color?: Color;
  /** Drawn centered when `variant` is `fallback`. */
  icon: IconName;
  /** Disambiguates the gradient element id when several tiles share a page. */
  idSeed?: string;
}>;

export function TilePreviewArt({ variant, color, icon, idSeed }: TilePreviewArtProps) {
  const instanceId = useId().replace(/:/g, "");
  const gradientId = `tile-art-${instanceId}${idSeed ? `-${idSeed.replace(/[^a-zA-Z0-9_-]/g, "-")}` : ""}`;
  const { lightest: t0, lighter: t1, base: t2, darker: t3 } = RAMP;

  return (
    <>
      <svg
        data-color={color ?? "grey"}
        width="100%"
        height="100%"
        viewBox="0 0 320 136"
        preserveAspectRatio="xMidYMid slice"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={t2} />
            <stop offset="100%" stopColor={t3} />
          </linearGradient>
        </defs>
        <rect width="320" height="136" fill={`url(#${gradientId})`} />
        <path
          d="M46 22h228a10 10 0 0 1 10 10v104H36V32a10 10 0 0 1 10-10Z"
          fill={FOREGROUND.body}
          stroke={FOREGROUND.rim}
        />
        {variant === "pie" ? (
          <>
            <path d="M160 45a34 34 0 1 1-28 53l28-19Z" fill={t0} />
            <path d="M160 45a34 34 0 0 1 33 25l-33 9Z" fill={t1} />
            <path d="M193 70a34 34 0 0 1-6 30l-27-21Z" fill={t2} />
            <path d="M187 100a34 34 0 0 1-55-2l28-19Z" fill={t3} />
          </>
        ) : null}
        {variant === "donut" ? (
          <>
            <circle cx="160" cy="79" r="34" fill="none" stroke={t0} strokeWidth="16" strokeDasharray="72 142" transform="rotate(-90 160 79)" />
            <circle cx="160" cy="79" r="34" fill="none" stroke={t1} strokeWidth="16" strokeDasharray="54 160" strokeDashoffset="-72" transform="rotate(-90 160 79)" />
            <circle cx="160" cy="79" r="34" fill="none" stroke={t2} strokeWidth="16" strokeDasharray="42 172" strokeDashoffset="-126" transform="rotate(-90 160 79)" />
            <circle cx="160" cy="79" r="18" fill={FOREGROUND.body} />
          </>
        ) : null}
        {variant === "bar" ? (
          <>
            {[96, 126, 156, 186, 216].map((x, i) => (
              <rect key={x} x={x} y={103 - i * 11} width="18" height={18 + i * 11} rx="5" fill={[t0, t1, t2, t3, t3][i]} />
            ))}
            <line x1="84" y1="108" x2="240" y2="108" stroke={FOREGROUND.track} strokeWidth="2" />
          </>
        ) : null}
        {variant === "line" ? (
          <>
            <path d="M78 106 C112 88 120 94 144 70 S186 74 212 52 S236 55 250 42 L250 116 L78 116Z" fill={t2} opacity="0.18" />
            <path d="M78 106 C112 88 120 94 144 70 S186 74 212 52 S236 55 250 42" fill="none" stroke={t1} strokeWidth="5" strokeLinecap="round" />
            <circle cx="144" cy="70" r="5" fill={t0} />
            <circle cx="212" cy="52" r="5" fill={t1} />
            <circle cx="250" cy="42" r="5" fill={t2} />
          </>
        ) : null}
        {variant === "battery" ? (
          <>
            <rect x="76" y="55" width="40" height="28" rx="7" fill={t0} />
            <rect x="121" y="55" width="40" height="28" rx="7" fill={t1} />
            <rect x="166" y="55" width="40" height="28" rx="7" fill={t2} />
            <rect x="211" y="55" width="32" height="28" rx="7" fill={t3} />
            <rect x="76" y="98" width="98" height="6" rx="3" fill={FOREGROUND.secondary} />
            <rect x="76" y="110" width="138" height="6" rx="3" fill={FOREGROUND.track} />
          </>
        ) : null}
        {variant === "metric" ? (
          <>
            <text x="160" y="80" textAnchor="middle" fill={FOREGROUND.loud} fontSize="28" fontWeight="700" fontFamily="Inter, ui-sans-serif, system-ui, sans-serif">1,380</text>
            <rect x="124" y="94" width="72" height="6" rx="3" fill={t2} opacity="0.9" />
          </>
        ) : null}
        {variant === "heatmap" ? (
          Array.from({ length: 42 }).map((_, i) => {
            const x = 84 + (i % 14) * 11;
            const y = 48 + Math.floor(i / 14) * 15;
            const fills = [FOREGROUND.track, FOREGROUND.secondary, t0, t1, t3];
            return <rect key={i} x={x} y={y} width="8" height="8" rx="2" fill={fills[(i * 7 + i) % fills.length]} opacity={i % 6 === 0 ? 0.55 : 0.95} />;
          })
        ) : null}
        {variant === "treemap" ? (
          <>
            <rect x="78" y="42" width="78" height="68" rx="7" fill={t0} />
            <rect x="164" y="42" width="78" height="30" rx="7" fill={t1} />
            <rect x="164" y="80" width="34" height="30" rx="7" fill={t2} />
            <rect x="206" y="80" width="36" height="30" rx="7" fill={t3} />
          </>
        ) : null}
        {variant === "list" ? (
          <>
            {[0, 1, 2, 3].map((i) => (
              <g key={i} transform={`translate(72 ${42 + i * 21})`}>
                {i > 0 ? <line x1="0" y1="-9" x2="176" y2="-9" stroke={FOREGROUND.hairline} /> : null}
                <circle cx="6" cy="0" r="4" fill={[t0, t1, t2, t3][i]} />
                <rect x="20" y="-4" width={82 - i * 6} height="8" rx="4" fill={FOREGROUND.label} />
                <rect x="134" y="-6" width={32 + i * 4} height="12" rx="6" fill={FOREGROUND.track} />
              </g>
            ))}
          </>
        ) : null}
        {variant === "activity" ? (
          <>
            {[0, 1, 2].map((i) => (
              <g key={i} transform={`translate(76 ${50 + i * 28})`}>
                <circle cx="10" cy="0" r="10" fill={[t0, t1, t3][i]} />
                <rect x="30" y="-8" width={118 - i * 10} height="7" rx="3.5" fill={FOREGROUND.label} />
                <rect x="30" y="5" width={82 + i * 8} height="6" rx="3" fill={FOREGROUND.secondary} />
              </g>
            ))}
          </>
        ) : null}
        {variant === "note" ? (
          <>
            <rect x="78" y="46" width="132" height="9" rx="4.5" fill={FOREGROUND.loud} />
            <rect x="78" y="67" width="166" height="6" rx="3" fill={FOREGROUND.label} />
            <rect x="78" y="81" width="142" height="6" rx="3" fill={FOREGROUND.secondary} />
            <circle cx="82" cy="101" r="3" fill={t2} />
            <rect x="92" y="98" width="96" height="6" rx="3" fill={FOREGROUND.label} />
            <circle cx="82" cy="115" r="3" fill={t3} />
            <rect x="92" y="112" width="128" height="6" rx="3" fill={FOREGROUND.secondary} />
          </>
        ) : null}
        {variant === "embed" ? (
          <>
            {WINDOW_DOTS.map((dot, i) => (
              <circle key={dot} cx={58 + i * 12} cy="43" r="3" fill={dot} />
            ))}
            <rect x="100" y="37" width="132" height="12" rx="6" fill={FOREGROUND.track} />
            <rect x="62" y="64" width="196" height="54" rx="8" fill={FOREGROUND.well} stroke={t3} />
          </>
        ) : null}
      </svg>
      {variant === "fallback" ? (
        <Box
          position="absolute"
          top={0}
          right={0}
          bottom={0}
          left={0}
          align="center"
          justify="center"
          data-color={color ?? "grey"}
          className="text-c-foreground"
        >
          <Icon name={icon} size="lg" />
        </Box>
      ) : null}
    </>
  );
}

/** A small rounded square filled with the deco color's gradient, the icon in
 *  that color's foreground on top. Grey when `color` is unset. */
export function TileIconSquare({
  icon,
  color,
  size = 40,
}: Omittable<{
  icon: IconName;
  color?: Color;
  size?: number;
}>) {
  const instanceId = useId().replace(/:/g, "");
  const gradientId = `tile-icon-${instanceId}`;

  return (
    <Box
      width={size}
      height={size}
      position="relative"
      data-color={color ?? "grey"}
      className="text-c-foreground"
    >
      <svg width={size} height={size} viewBox="0 0 40 40" aria-hidden="true">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor={RAMP.base} />
            <stop offset="100%" stopColor={RAMP.darker} />
          </linearGradient>
        </defs>
        <rect width="40" height="40" rx="10" fill={`url(#${gradientId})`} />
      </svg>
      <Box
        position="absolute"
        top={0}
        right={0}
        bottom={0}
        left={0}
        align="center"
        justify="center"
      >
        <Icon name={icon} size="sm" />
      </Box>
    </Box>
  );
}
